import { callAdminApi } from './wixAdminApi';
import { StaffMember, BookingItem } from '../../types/bookings';

export class StaffApi {
  /**
   * Get all staff members with optional additional fields
   */
  async getAllStaffMembers(options?: {
    includeResourceDetails?: boolean;
    includeAssociatedIdentityStatus?: boolean;
  }): Promise<StaffMember[]> {
    try {
      // (Full-structure JSON dumps of every staff member were removed from
      // here — they ran on every call at startup and stalled the main thread.)
      const result = await callAdminApi('queryStaffMembers', 'staff', options);
      return (result as { _items?: StaffMember[] })?._items || [];
    } catch (error) {
      console.error('❌ StaffApi.getAllStaffMembers error:', error);
      return [];
    }
  }

  /**
   * Get staff member by ID
   */
  async getStaffMemberById(id: string): Promise<StaffMember | null> {
    try {
      const result = await callAdminApi('getStaffMember', 'staff', { id });
      return result as StaffMember;
    } catch (error) {
      console.error('Error getting staff member by ID:', error);
      return null;
    }
  }

  /**
   * Query staff members with filters and optional fields
   */
  async queryStaffMembers(params: {
    filter?: Partial<StaffMember> & {
      [key: string]: string | number | boolean | string[] | { $contains?: string; $eq?: string; $ne?: string; $in?: string[] };
    };
    limit?: number;
    skip?: number;
    sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
    fields?: ('ASSOCIATED_IDENTITY_STATUS' | 'RESOURCE_DETAILS')[];
  }): Promise<{ items: StaffMember[]; totalCount?: number }> {
    try {
      const result = await callAdminApi('queryStaffMembers', 'staff', params);
      const response = result as { _items?: StaffMember[]; _totalCount?: number };
      return { 
        items: response._items || [], 
        totalCount: response._totalCount 
      };
    } catch (error) {
      console.error('Error querying staff members:', error);
      return { items: [] };
    }
  }

  /**
   * Create a new staff member
   */
  async createStaffMember(staffData: Partial<StaffMember>): Promise<StaffMember> {
    try {
      const result = await callAdminApi('createStaffMember', 'staff', staffData);
      return result as StaffMember;
    } catch (error) {
      console.error('Error creating staff member:', error);
      throw new Error('Failed to create staff member');
    }
  }

  /**
   * Update a staff member
   */
  async updateStaffMember(id: string, staffData: Partial<StaffMember>): Promise<StaffMember> {
    try {
      const payload: Record<string, unknown> = { id, ...staffData };

      // Wix staff update requires revision; fetch the latest record when caller did not provide it.
      if (payload.revision === undefined || payload.revision === null || payload.revision === '') {
        const existing = await this.getStaffMemberById(id);
        const existingRecord = existing as Record<string, unknown> | null;
        const revision = existingRecord?.revision ?? existingRecord?._revision;

        if (revision !== undefined && revision !== null && revision !== '') {
          payload.revision = revision;
        }
      }

      if (payload.revision === undefined || payload.revision === null || payload.revision === '') {
        throw new Error(`Missing staff revision for update (${id})`);
      }

      const result = await callAdminApi('updateStaffMember', 'staff', payload);
      return result as StaffMember;
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw new Error('Failed to update staff member');
    }
  }

  /**
   * Delete a staff member
   */
  async deleteStaffMember(id: string): Promise<boolean> {
    try {
      await callAdminApi('deleteStaffMember', 'staff', { id });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const isAlreadyDeleted = message.includes('NOT_FOUND') || message.includes('Entity not found');
      if (isAlreadyDeleted) {
        console.warn(`Staff member ${id} already deleted in Wix; continuing with cache cleanup.`);
        return true;
      }

      console.error('Error deleting staff member:', error);
      return false;
    }
  }

  /**
   * Get staff member availability - returns booking items for availability
   */
  async getStaffMemberAvailability(staffMemberId: string, startDate: Date, endDate: Date): Promise<BookingItem[]> {
    try {
      const result = await callAdminApi('getStaffMemberAvailability', 'staff', {
        staffMemberId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      return (result as { _items?: BookingItem[] })?._items || [];
    } catch (error) {
      console.error('Error getting staff member availability:', error);
      return [];
    }
  }

  /**
   * Search staff members by name or other criteria
   */
  async searchStaffMembers(query: string): Promise<StaffMember[]> {
    try {
      const result = await callAdminApi('queryStaffMembers', 'staff', {
        filter: {
          name: { $contains: query }
        }
      });
      return (result as { _items?: StaffMember[] })?._items || [];
    } catch (error) {
      console.error('Error searching staff members:', error);
      return [];
    }
  }
}
